import React, { useMemo } from "react";

const PrayerTimesPanel = ({
  highlightColor = "#222222",
  labelTextColor = "#ffffff",
  timingTextColor = "#00FF00",
  textColor = "#ffffff",
  prayerData,
  nextEvent,
  isJummahMode
}) => {
  
  const prayers = useMemo(() => {
    if (!prayerData) return [];
    
    const iqama = prayerData.iqamatimes || {};
    const adhan = prayerData.startTimes?.timings || {};

    return [
      { key: "fajr", urdu: "فجر", english: "Fajr", iqama: iqama.fajr, start: adhan.fajr },
      { key: "dhuhr", urdu: "ظہر", english: "Dhuhr", iqama: iqama.dhuhr, start: adhan.dhuhr },
      { key: "asr", urdu: "عصر", english: "Asr", iqama: iqama.asr, start: adhan.asr },
      { key: "maghrib", urdu: "مغرب", english: "Maghrib", iqama: iqama.maghrib, start: adhan.maghrib },
      { key: "isha", urdu: "عشاء", english: "Ishaa", iqama: iqama.isha, start: adhan.isha },
    ];
  }, [prayerData]);

  const activeKey = useMemo(() => {
    if (isJummahMode) return null;
    if (nextEvent) {
      const name = (nextEvent.name || "").toLowerCase();
      if (name === "no_highlight") return null;
      if (["fajr", "dhuhr", "asr", "maghrib", "isha"].includes(name)) {
        return name;
      }
    }
    return null;
  }, [nextEvent, isJummahMode]);

  const formatTime = (timeStr) => {
    if (!timeStr) return "—";
    const clean = timeStr.replace(/(AM|PM)/i, "").trim();
    const [h, m] = clean.split(":");
    return { formatted: `${parseInt(h, 10)}:${m}` };
  };

  if (!prayers.length) return <div style={{ color: textColor }} className="p-4">Loading...</div>;

  return (
    <div className="flex flex-col w-full h-full px-0">
      
      {/* --- HEADER ROW --- */}
      <div className="flex w-full justify-end px-0 mb-0">
         <div 
            className="text-right w-[35%] font-bold text-[2.5svh] opacity-800"
            style={{ color: labelTextColor }}
         >
            Beginning Time
         </div>
      </div>

      {/* --- PRAYER ROWS LOOP --- */}
      <div className="flex flex-col w-full h-full justify-between">
        {prayers.map((p) => {
          const isCurrent = p.key === activeKey;
          const iqamaTime = formatTime(p.iqama);
          const startTime = formatTime(p.start);

          return (
            <div
              key={p.key}
              className="flex w-full justify-between items-center px-[0.5svh] py-[1.5svh] transition-all duration-300"
              style={{
                backgroundColor: isCurrent ? highlightColor : "transparent", borderRadius: "1svh"
              }}
            >
              
              {/* --- LEFT SIDE (Urdu + Green Time) --- */}
              {/* Added min-w to ensure stability */}
              <div className="flex flex-col items-center w-auto min-w-[5%]"> 
                {/* Urdu Name - Removed Bold, Reduced Size */}
                <div 
                  className="font-sans font-semibold leading-none mb-3" // font-normal
                  style={{ 
                    color: textColor, 
                    fontSize: "3.5svh" // Reduced
                  }}
                >
                  {p.urdu}
                </div>
                
                {/* Iqamah Time - Reduced Size */}
                <div
                  className="font-[Digital-7] leading-[0.8]"
                  style={{ 
                      fontSize: "8.7svh", // Reduced
                      color: timingTextColor, 
                      textShadow: isCurrent ? `0px 0px 4px ${timingTextColor}66` : "none" 
                  }}
                >
                  {iqamaTime.formatted}
                </div>
              </div>

              {/* --- RIGHT SIDE (English + White Time) --- */}
              <div className="flex flex-col items-end w-[40%]">
                {/* English Name - Increased Size */}
                <div 
                  className="font-sans font-semibold leading-none mb-1"
                  style={{ 
                    color: textColor, 
                    fontSize: "4.8svh" // Increased
                  }}
                >
                  {p.english}
                </div>

                {/* Start Time - Increased Size */}
                <div
                  className="font-[Digital-7] leading-[0.9]"
                  style={{ 
                      color: textColor,
                      fontSize: "5.5svh" // Increased
                  }}
                >
                  {startTime.formatted}
                </div>
              </div>

            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PrayerTimesPanel;